<template>
  <nav aria-label="Progress" class="mb-6">
    <ol role="list" class="overflow-hidden">
      <li
        v-for="(step, stepIdx) in steps"
        :key="step.name"
        :class="[stepIdx !== steps.length - 1 ? 'pb-10' : '', 'relative']"
      >
        <!-- TODO: Separate to its own component -->
        <template v-if="step.status === 'complete'">
          <div
            v-if="stepIdx !== steps.length - 1"
            class="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-vice-teal-400"
            aria-hidden="true"
          />
          <div class="relative flex items-start group">
            <span class="h-9 flex items-center">
              <span
                class="
                  relative
                  z-10
                  w-8
                  h-8
                  flex
                  items-center
                  justify-center
                  bg-vice-teal-400
                  rounded-full
                  group-hover:bg-vice-teal-600
                "
              >
                <CheckIcon class="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span class="ml-4 min-w-0 flex flex-col flex-grow select-none">
              <span class="text-xs text-vice-teal-700 font-semibold tracking-wide uppercase">
                {{ step.name }}
              </span>
              <span class="text-sm text-gray-500">{{ step.description }}</span>
            </span>

            <button
              class="
                mx-3
                inline-flex
                items-center
                px-6
                border border-transparent
                rounded-md
                shadow-sm
                text-sm
                font-medium
                text-white
                bg-vice-teal-400
                hover:bg-vice-teal-500
                focus:outline-none
                focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                h-11
                self-end
              "
              v-if="step.action.complete"
              @click="step.action.complete.method"
            >
              {{ step.action.complete.text }}
            </button>
          </div>
        </template>

        <template v-else-if="step.status === 'current'">
          <div
            v-if="stepIdx !== steps.length - 1"
            class="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
            aria-hidden="true"
          />
          <div class="relative flex items-center group py-2" aria-current="step">
            <span class="h-9 flex items-center" aria-hidden="true">
              <span
                class="
                  relative
                  z-10
                  w-8
                  h-8
                  flex
                  items-center
                  justify-center
                  bg-white
                  border-2 border-vice-teal-400
                  rounded-full
                "
              >
                <span class="h-2.5 w-2.5 bg-vice-teal-400 rounded-full" />
              </span>
            </span>

            <span class="mx-4 min-w-0 flex flex-col flex-grow select-none">
              <span class="text-xs font-semibold tracking-wide uppercase text-vice-teal-500">
                {{ step.name }}
              </span>
              <span class="text-sm text-gray-500">{{ step.description }}</span>
            </span>

            <button
              class="
                mx-3
                inline-flex
                items-center
                px-6
                border border-transparent
                rounded-md
                shadow-sm
                text-sm
                font-medium
                text-white
                bg-vice-teal-400
                hover:bg-vice-teal-500
                focus:outline-none
                focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                h-11
                self-end
              "
              @click="step.action.current.method"
            >
              {{ step.action.current.text }}
            </button>
          </div>
        </template>

        <template v-else>
          <div
            v-if="stepIdx !== steps.length - 1"
            class="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
            aria-hidden="true"
          />
          <div class="relative flex items-center group py-2">
            <span class="h-9 flex items-center" aria-hidden="true">
              <span
                class="
                  relative
                  z-10
                  w-8
                  h-8
                  flex
                  items-center
                  justify-center
                  bg-white
                  border-2 border-gray-300
                  rounded-full
                  group-hover:border-gray-400
                "
              >
                <span class="h-2.5 w-2.5 bg-transparent rounded-full group-hover:bg-gray-300" />
              </span>
            </span>
            <span class="ml-4 min-w-0 flex flex-col flex-grow select-none">
              <span class="text-xs font-semibold tracking-wide uppercase text-gray-500">
                {{ step.name }}
              </span>
              <span class="text-sm text-gray-500">{{ step.description }}</span>
            </span>

            <button
              class="
                mx-3
                inline-flex
                items-center
                px-6
                border border-transparent
                rounded-md
                shadow-sm
                text-sm
                font-medium
                h-11
                self-end
                pointer-events-none
                text-gray-500
                bg-gray-150
                select-none
              "
              @click="step.action.current.method"
            >
              {{ step.action.current.text }}
            </button>
          </div>
        </template>
      </li>
    </ol>
  </nav>

  <participant-learning-goals-modal
    :open="isOpenLearningGoalsModal"
    @learningGoalsAdded="goToNextStep"
    @closeLearningGoalsModal="isOpenLearningGoalsModal = false"
  ></participant-learning-goals-modal>

  <participant-entry-modal
    :open="isOpenEntryModal"
    @closeEntryModal="isOpenEntryModal = false"
  ></participant-entry-modal>
</template>

<script>
import { ref } from 'vue';
import { CheckIcon } from '@heroicons/vue/solid';
import { usePage } from '@inertiajs/inertia-vue3';
import ParticipantEntryModal from './ParticipantEntryModal.vue';
import ParticipantLearningGoalsModal from './ParticipantLearningGoalsModal.vue';
import useTimeLeft from '@/Utils/dateTimeFormatter';

export default {
  name: 'SessionEntrySteps',
  components: {
    ParticipantEntryModal,
    ParticipantLearningGoalsModal,
    CheckIcon,
  },
  setup() {
    const pageProps = usePage().props.value;

    const isOpenEntryModal = ref(false);
    const isOpenLearningGoalsModal = ref(false);

    const openEntryModal = () => {
      isOpenEntryModal.value = true;
    };

    const openPersonalGoalsModal = () => {
      isOpenLearningGoalsModal.value = true;
    };

    const updateTimeLeft = () => {
      pageProps.session.details.time_left = useTimeLeft(
        pageProps.session.details.session_start_by_timezone,
      );
    };
    updateTimeLeft();
    setInterval(() => {
      updateTimeLeft();
    }, 1000);

    const steps = [
      {
        name: 'Participation accepted',
        description: 'You have accepted the invitation',
        href: '#',
        status: 'complete',
        action: {},
      },
      {
        name: 'Learning goals',
        description: 'Set your personal learning goals to enter the session.',
        href: '#',
        status: 'current',
        action: {
          complete: {
            text: 'Edit goals',
            method: openPersonalGoalsModal,
          },
          current: {
            text: 'Set goals',
            method: openPersonalGoalsModal,
          },
        },
      },
      {
        name: 'Session',
        description: pageProps?.session.details.time_left
          ? `Session starts in ${pageProps.session.details.time_left}`
          : 'Session has already started!',
        href: '#',
        status: 'upcoming',
        action: {
          upcoming: {
            text: 'Join session',
            method: openEntryModal,
          },
          current: {
            text: 'Enter session',
            method: openEntryModal,
          },
        },
      },
    ];

    const goToNextStep = () => {
      const currentStepIndex = steps.findIndex((step) => step.status == 'current');
      if (currentStepIndex == steps.length - 1) return;
      steps[currentStepIndex].status = 'complete';
      steps[currentStepIndex + 1].status = 'current';
    };

    if (pageProps?.session?.reflection?.my_goals) {
      goToNextStep();
    }

    return {
      steps,
      isOpenEntryModal,
      isOpenLearningGoalsModal,
      openEntryModal,
      goToNextStep,
    };
  },

  mounted() {
    // this.humanizeTime();
  },
  methods: {},
};
</script>

<style scoped></style>
